<template>
    <form class="form-signin" data-test-id="form-confirm-password" @submit.prevent="confirm">
        <div class="login-card">
            <header class="login-header">
                <img class="login-logo" width="200" src="/images/logo-lead.png" :alt="themeBrandName" />
                <div class="login-heading-row">
                    <h5 class="login-title">{{ $tl("labels.form.confirmPassword.title") }}</h5>
                    <ChooseLang class="login-lang" />
                </div>
            </header>

            <div v-if="errorMessage" id="confirm-password-error" class="alert alert-danger login-alert" role="alert">
                {{ errorMessage }}
            </div>

            <p class="text-muted mb-4">
                {{ $tl(instructionsKey) }}
            </p>

            <div v-if="!requiresFreshLogin" class="login-fields">
                <div class="login-field">
                    <label for="confirm-password" class="form-label login-label">{{ $tl("labels.form.confirmPassword.password") }}</label>
                    <input
                        id="confirm-password"
                        ref="passwordInput"
                        v-model="password"
                        data-test-id="input-confirm-password"
                        type="password"
                        class="form-control"
                        :class="{ 'is-invalid': errorMessage }"
                        :aria-invalid="errorMessage ? 'true' : 'false'"
                        :aria-describedby="errorMessage ? 'confirm-password-error' : undefined"
                        autocomplete="current-password"
                        required
                        autofocus
                        @input="errorMessage = null" />
                </div>
            </div>

            <button data-test-id="btn-confirm-password" type="submit" class="btn btn-success btn-circle login-submit" :disabled="submitting">
                {{ $tl(confirmKey) }}
            </button>

            <div class="login-register-link mfa-account-switch">
                <button type="button" class="btn btn-link p-0" :disabled="loggingOut" @click="logout">
                    {{ $tl("labels.form.mfa.useAnotherAccount") }}
                </button>
            </div>
        </div>
    </form>
</template>

<script>
import axios from "axios";
import ChooseLang from "../../components/Default/ChooseLang.vue";

export default {
    name: "ConfirmPassword",
    components: { ChooseLang },
    computed: {
        reauthenticationReason() {
            return new URLSearchParams(window.location.search).get("reauthentication");
        },
        requiresFreshLogin() {
            return ["external", "admin"].includes(this.reauthenticationReason);
        },
        instructionsKey() {
            if (this.reauthenticationReason === "admin") {
                return "labels.form.confirmPassword.adminInstructions";
            }

            return this.requiresFreshLogin ? "labels.form.confirmPassword.externalInstructions" : "labels.form.confirmPassword.instructions";
        },
        confirmKey() {
            if (this.reauthenticationReason === "admin") {
                return "labels.form.confirmPassword.adminConfirm";
            }

            return this.requiresFreshLogin ? "labels.form.confirmPassword.externalConfirm" : "labels.form.confirmPassword.confirm";
        },
    },
    data() {
        return {
            password: "",
            errorMessage: null,
            submitting: false,
            loggingOut: false,
        };
    },
    methods: {
        confirm() {
            if (this.requiresFreshLogin) {
                this.logout();
                return;
            }

            this.submitting = true;
            this.errorMessage = null;

            axios
                .post("/password/confirm", {
                    password: this.password,
                    return_url: new URLSearchParams(window.location.search).get("return_url"),
                })
                .then((response) => {
                    const redirectUrl = response.data.redirect_url || "/";
                    window.location.replace(redirectUrl);
                })
                .catch((error) => {
                    this.errorMessage = error.response?.data?.message || this.$tl("labels.form.confirmPassword.invalid");
                    this.password = "";
                    this.$nextTick(() => this.$refs.passwordInput?.focus());
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
        logout() {
            this.loggingOut = true;

            axios
                .post("/logout")
                .then((response) => {
                    window.location.replace(response.data.redirect_url || response.headers.location || "/login");
                })
                .catch((error) => {
                    this.errorMessage = error.response?.data?.message || this.$tl("labels.form.confirmPassword.logoutError");
                })
                .finally(() => {
                    this.loggingOut = false;
                });
        },
    },
};
</script>
